/**
 * CDD Records API Configuration
 * Static maps and helper functions extracted from cddRecordsApi.ts
 */

import {
	CheckType,
	VerificationStatus
} from '../types/cdd-records.types';
import { logger } from '../utils/logger';

// ─── Role Type Mapping ─────────────────────────────────────────────────────

/** Map backend roleType → AssociatedPersonRole */
export const ROLE_TYPE_MAP: Record<string, string> = {
	PRINCIPAL: 'director',
	DIRECTOR: 'director',
	UBO: 'beneficial-owner',
	BENEFICIAL_OWNER: 'beneficial-owner',
	/**
	 * AL-529: was `'shareholder'`, and that was the actual defect.
	 *
	 * Every registry role arrives as `roleType: "ASIC"` with the office in
	 * `roleDetail`. `mapAssociatedPersonRole` special-cases DIRECTOR, SHAREHOLDER
	 * and now SECRETARY; anything else reached this entry and came out asserting a
	 * shareholding. A company secretary was reported to the C1 as a Shareholder.
	 *
	 * It must stay `'other'` rather than being narrowed to the three known offices.
	 * SimpleKYC can return others — PUBLIC_OFFICER and the like — and the next
	 * unmapped one has to degrade to "Other", not to a claim about ownership. The
	 * label and a slate colour already exist for it.
	 */
	ASIC: 'other',
	/**
	 * Nothing produces this id any more, which is correct: it has never meant what
	 * it says. The genuine ASIC+SHAREHOLDER role maps to `beneficial-owner`, which
	 * is AL-203's ownership-versus-control framing and a separate compliance
	 * question from this ticket. Left in place rather than deleted so a future
	 * `roleType: "SHAREHOLDER"` would land somewhere sane.
	 */
	SHAREHOLDER: 'shareholder',
	SECRETARY: 'secretary',
	TRUSTEE: 'trustee',
	BENEFICIARY: 'beneficiary',
	SETTLOR: 'settlor',
	APPOINTER: 'appointer',
	NOMINEE: 'nominee',
	AUTHORISED_SIGNATORY: 'authorised-signatory',
	/**
	 * AL-519 item 1: these two were BOTH 'authorised-representative', which is the
	 * whole defect. They are unrelated facts:
	 *
	 *   APPROVED_USER — nominated to use the designated service AFTER onboarding
	 *   C2_USER       — completed the onboarding form FOR the entity
	 *
	 * One is about using the service later, the other about who filled in the form.
	 * Collapsing them is what put the tag on Tony Azzam while Joanne Herron, the
	 * person actually authorised, rendered as "Director" (AL-510, via Justin).
	 *
	 * Neither label says "Authorised Representative" any more. That phrase is the
	 * cause of the ticket: a C1 reads it as "authorised to act", while the stored
	 * data meant "approved to use the service". Authority to act is now a separate
	 * person-level marker, not a role chip — see authorityBasis in cddRecordsApi.
	 */
	APPROVED_USER: 'approved-user',
	PARTNER: 'partner',
	COMMITTEE_MEMBER: 'committee-member',
	OFFICE_BEARER: 'office-bearer',
	KEY_CONTROLLER: 'key-controller',
	C2_USER: 'application-contact',
};

/**
 * AL-519 item 7 — entity types where "Senior Managing Official" is the accurate
 * term for a UBO role, rather than "Beneficial Owner".
 *
 * `UBO` mapped to `beneficial-owner` unconditionally, so Carolyn read as a
 * "Beneficial Owner" on an incorporated association, which does not have
 * beneficial owners in that sense (AL-510 point 4). Both the panel heading and
 * the PDF summary already hedged "Beneficial Owners or Senior Managing Official";
 * only the per-person tag never did.
 *
 * ⚠️ COMPLIANCE SIGN-OFF REQUIRED on the membership of this set.
 *
 * It deliberately contains ONLY `association`, because that is the single entity
 * type with evidence behind it — the reported case. `government-body`,
 * `registered-body` and `partnership` are all plausible candidates and are
 * deliberately NOT included: the ticket is explicit that this must not be
 * inferred from the entity-type enum, and widening it on my own judgement would
 * relabel people on live records with no authority for it.
 *
 * Adding an entity type here is a one-line change once compliance answers.
 */
export const SENIOR_MANAGING_OFFICIAL_ENTITY_TYPES: ReadonlySet<string> = new Set([
	'association',
]);

/**
 * The two role ids that mean "ultimately owns or controls this entity".
 *
 * AL-519 item 7 introduces a SECOND id for one underlying role, which quietly
 * breaks every consumer written as `roles.includes('beneficial-owner')`. There
 * were four of those — the structure panel, the PDF Key Personnel section, the
 * record-detail role summary and the trust beneficiary rollup — and each would
 * have started returning an empty list for associations while looking fine for
 * companies.
 *
 * So the predicate is shared, not repeated. Adding a third id later is a change
 * here and nowhere else.
 */
export const BENEFICIAL_OWNER_ROLE_IDS: readonly string[] = [
	'beneficial-owner',
	'senior-managing-official',
];

export const isBeneficialOwnerRole = (role: string): boolean =>
	BENEFICIAL_OWNER_ROLE_IDS.includes(role);

/**
 * Heading for the population above, named for the entity type.
 *
 * Replaces the hedged "Beneficial Owners or Senior Managing Official" that the
 * panel and the PDF both carried. The hedge existed because the per-person tag
 * was always wrong for one of the two cases; now that the tag is accurate the
 * heading can be too.
 */
export const beneficialOwnerHeading = (entityType?: string): string =>
	entityType && SENIOR_MANAGING_OFFICIAL_ENTITY_TYPES.has(entityType)
		? 'Senior Managing Officials'
		: 'Beneficial Owners';

// ─── Check Type Mapping ────────────────────────────────────────────────────

/** Map backend checkType → CDDCheck CheckType */
export const CHECK_TYPE_MAP: Record<string, CheckType> = {
	KYC_IDENTITY: 'identity-verification',
	KYB_ENTITY: 'entity-verification',
	SANCTIONS: 'pep-screening',
	PEP_TFS: 'pep-screening',
};

// ─── Risk Factor Labels ────────────────────────────────────────────────────

/** Map backend risk factor keys → display labels */
export const RISK_FACTOR_LABEL_MAP: Record<string, string> = {
	countryRisk: 'Country Risk',
	industryRisk: 'Industry Risk',
	productRisk: 'Product / Service Risk',
	customerRisk: 'Customer Risk',
};

// ─── Helper Functions ──────────────────────────────────────────────────────

// ─── KYC status classification ─────────────────────────────────────────────

/**
 * AL-523: what a backend KYC status means, in one place.
 *
 * Two mappers used to enumerate these by hand — `mapKycStatus` here and `mapKycDocStatus` in
 * cddRecordsApi.ts — and they had already drifted: neither knew `OPS_VERIFIED`, and only one knew
 * `DEDUP_LINKED`. Both ended in a bare `return 'pending'`, so a status nobody had mapped quietly became
 * "pending" and looked plausible.
 *
 * That is how Istvan Dobo on IDEN.6ZJf.0XF5.ut4L.zcMY.RS2S came to read as Pending with a complete
 * evidence trail behind him — COMPLETE_OVERRIDE, OPSPASS and a recorded IDVerse reference. The server did
 * its job and reported OPS_VERIFIED; nothing here recognised the word. Ten people in the demo copy of
 * production were affected. Reported by Ash, 2026-10-02.
 *
 * One table, two projections. Add a status here and both mappers learn it.
 */
export type KycOutcome = 'verified' | 'failed' | 'pending' | 'not-started';

/**
 * Every status the server can put on `identityCheck.kycStatus`, and nothing it cannot.
 *
 * Three sources feed it, which is why the list is longer than any single enum:
 *   - `kyc_data.result_status`, i.e. KYCStatusType: NOTSTARTED, STARTED, PASSEDCLEAN, PASSEDMANUAL,
 *     FAILEDKYCCHECK, FAILEDMISMATCH, FAILEDTECHNICAL
 *   - `assembleIdentityCheck`'s fallback where no kyc_data row exists: OPS_VERIFIED or PENDING
 *   - `emptyIdentityCheck`, which echoes the ROLE ROW's kyc_status for a person with no child case:
 *     PASSED, DEDUP_LINKED, PENDING, TERMINATED, NOT_STARTED, FAILEDTERMINAL
 *
 * kycStatusVocabulary.test.ts pins this against that list, so a new server status with no entry here
 * fails the build instead of silently becoming "pending".
 */
export const KYC_STATUS_OUTCOME: Record<string, KycOutcome> = {
	// Verified. OPS_VERIFIED is only ever emitted where an audited COMPLETE_OVERRIDE exists — AG-351
	// made the server require that evidence rather than infer verification from a finished case.
	PASSEDCLEAN: 'verified',
	PASSEDMANUAL: 'verified',
	PASSED: 'verified',
	OPS_VERIFIED: 'verified',

	// DEDUP_LINKED means this person's verification lives on ANOTHER case, so the proof is deliberately
	// not in the place this status is read from. Kept as 'verified', unchanged from before AL-523.
	//
	// It is the weakest entry in this table and it is left alone on purpose. AL-515 and AL-517 both found
	// rows whose dedup_source points at a case holding no result, so it does not reliably mean verified —
	// but 49 role rows in the demo copy of production reach the client this way, and demoting them would
	// assert that those people are unverified on the strength of a table that was never where the evidence
	// lived. Absence of a local record is not absence of the check. The real fix is server-side: resolve
	// dedup_source and report the source case's actual result instead of echoing the link. Not this change.
	DEDUP_LINKED: 'verified',

	// Failed. The only failure branch here used to be `kycStatus === 'FAILED'` — a value the server cannot
	// emit at all: it is absent from KYCStatusType and the Postgres kyc_status enum rejects it outright
	// ("invalid input value for enum kyc_status"). So the branch was unreachable and every real failure
	// fell through to 'pending', presenting a failed identity check as work still outstanding. The orphan
	// check in kycStatusVocabulary.test.ts is what surfaced it.
	FAILEDKYCCHECK: 'failed',
	FAILEDMISMATCH: 'failed',
	FAILEDTECHNICAL: 'failed',
	FAILEDTERMINAL: 'failed',

	// In flight.
	STARTED: 'pending',
	PENDING: 'pending',

	// Not begun.
	NOTSTARTED: 'not-started',
	NOT_STARTED: 'not-started',

	// AL-515 excludes terminated people from the report, so this should never reach a client. Mapped
	// explicitly rather than left to a fallback: if it ever does arrive, 'pending' is the conservative
	// reading, and a terminated person must never read as verified.
	TERMINATED: 'pending',
};

/**
 * Classify a backend KYC status. An unrecognised value is treated as 'pending' — never verified — and
 * logged, because the silent version of this fallback is what AL-523 was.
 */
export function kycOutcome(kycStatus: string): KycOutcome {
	const known = KYC_STATUS_OUTCOME[kycStatus];
	if (known) return known;
	if (kycStatus) {
		logger.warn(
			`Unmapped KYC status "${kycStatus}" from the server — treating as pending. ` +
				'Add it to KYC_STATUS_OUTCOME; see AL-523.',
		);
	}
	return 'pending';
}

/** Map backend KYC status string to VerificationStatus */
export function mapKycStatus(kycStatus: string): VerificationStatus {
	return kycOutcome(kycStatus);
}
